import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Button, Icon, Tag, message } from '@common/musicHubCommon';
import { ROUTES } from '../../../routes';
import './index.scss';

interface Album {
  id: string;
  title: string;
  artist: string;
  tracks: number;
  cover: string;
  genres: string[];
  releaseDate: string;
  status: 'Under Review' | 'Released' | 'Rejected';
  songs?: SongInAlbum[];
}

interface SongInAlbum {
  id: string;
  title: string;
  artist: string;
  album: string;
  cover: string;
  genres: string[];
  releaseDate: string;
  status: 'Under Review' | 'Released' | 'Rejected';
}

// 拖拽图标
const DragIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="white">
    <circle cx="9" cy="6" r="1.5" />
    <circle cx="15" cy="6" r="1.5" />
    <circle cx="9" cy="12" r="1.5" />
    <circle cx="15" cy="12" r="1.5" />
    <circle cx="9" cy="18" r="1.5" />
    <circle cx="15" cy="18" r="1.5" />
  </svg>
);

// 编辑图标 - 外部链接样式
const EditIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path d="M18 13V19C18 19.5304 17.7893 20.0391 17.4142 20.4142C17.0391 20.7893 16.5304 21 16 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V8C3 7.46957 3.21071 6.96086 3.58579 6.58579C3.96086 6.21071 4.46957 6 5 6H11" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M15 3H21V9" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M10 14L21 3" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const AlbumInfo: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // 从 location.state 或 sessionStorage 获取专辑数据
  const [album, setAlbum] = useState<Album | null>(() => {
    if (location.state?.album) {
      return location.state.album;
    }
    const stored = sessionStorage.getItem('currentAlbum');
    return stored ? JSON.parse(stored) : null;
  });

  const [songs, setSongs] = useState<SongInAlbum[]>(() => {
    if (location.state?.album?.songs) {
      return location.state.album.songs;
    }
    const stored = sessionStorage.getItem('currentAlbum');
    if (stored) {
      const albumData = JSON.parse(stored);
      return albumData.songs || [];
    }
    return [];
  });

  // 拖拽状态
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);

  // 保存到 sessionStorage
  useEffect(() => {
    if (album) {
      const albumWithSongs = { ...album, songs };
      sessionStorage.setItem('currentAlbum', JSON.stringify(albumWithSongs));
    }
  }, [album, songs]);

  // 处理返回
  const handleBack = () => {
    navigate(ROUTES.myAlbums);
  };

  // 移动歌曲
  const moveSong = useCallback((dragIndex: number, hoverIndex: number) => {
    setSongs(prevSongs => {
      const newSongs = [...prevSongs];
      const [draggedSong] = newSongs.splice(dragIndex, 1);
      newSongs.splice(hoverIndex, 0, draggedSong);
      return newSongs;
    });
  }, []);

  // 拖拽开始
  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  // 拖拽结束
  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  // 拖拽经过
  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex !== null && draggedIndex !== index) {
      moveSong(draggedIndex, index);
      setDraggedIndex(index);
    }
  };

  // 编辑专辑信息
  const handleEditAlbumInfo = () => {
    navigate(`/album/${id}/edit`, {
      state: {
        album,
        returnTo: `/album/${id}/info`,
      },
    });
  };

  // 编辑歌曲
  const handleEditSong = (index: number) => {
    const song = songs[index];
    navigate(`/song/${song.id}/edit`, {
      state: {
        song: {
          id: song.id,
          title: song.title,
          album: album?.title || '',
          cover: song.cover || album?.cover || '',
          genres: song.genres || [],
          releaseDate: song.releaseDate || '',
          status: song.status,
          artists: [song.artist],
        },
        returnTo: `/album/${id}/info`,
      },
    });
  };

  // 添加更多歌曲
  const handleAddMoreSongs = () => {
    // 保存返回信息
    sessionStorage.setItem('addSongReturnTo', `/album/${id}/info`);
    sessionStorage.setItem('addSongReturnState', JSON.stringify({ album }));
    sessionStorage.setItem('uploadType', 'album');
    sessionStorage.setItem('albumData', JSON.stringify({
      albumName: album?.title || '',
      coverArtPreview: album?.cover || '',
      artist: album?.artist || '',
      genres: album?.genres || [],
    }));
    
    navigate(ROUTES.releaseUpload);
  };

  // 保存
  const handleSave = () => {
    console.log('Save album changes', { album, songs });
    message.success('Album saved successfully');
    navigate(ROUTES.myAlbums);
  };

  if (!album) {
    return null;
  }

  return (
    <div className="album-info">
      {/* 页面标题 */}
      <div className="album-info__header">
        <button className="back-btn" onClick={handleBack}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
          </svg>
          <span>Album Information</span>
        </button>
      </div>

      {/* 主内容 */}
      <div className="album-info__content">
        <div className="album-info__card">
          {/* 专辑信息卡片 */}
          <div className="album-card">
            {/* 编辑按钮 - 右上角 */}
            <button className="album-card__edit" onClick={handleEditAlbumInfo}>
              <EditIcon />
            </button>
            
            {/* 专辑内容 */}
            <div className="album-card__inner">
              <div className="album-card__cover">
                {album.cover ? (
                  <img src={album.cover} alt="Album cover" />
                ) : (
                  <div className="album-card__placeholder">
                    <svg width="40" height="40" viewBox="0 0 24 24" fill="rgba(255,255,255,0.3)">
                      <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z"/>
                    </svg>
                  </div>
                )}
              </div>
              <div className="album-card__details">
                <span className="album-card__title">{album.title}</span>
                <span className="album-card__artist">{album.artist}</span>
                <span className="album-card__tracks">{songs.length} {songs.length === 1 ? 'track' : 'tracks'}</span>
                <div className="album-card__genres">
                  {album.genres?.map((genre, index) => (
                    <Tag key={index}>{genre}</Tag>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* 曲目列表标题 */}
          <h2 className="album-info__title">Album Track List</h2>

          {/* 歌曲列表 */}
          {songs.length > 0 ? (
            <div className="track-list">
              {songs.map((song, index) => (
                <div
                  key={song.id || index}
                  className={`track-item ${draggedIndex === index ? 'track-item--dragging' : ''}`}
                  draggable
                  onDragStart={() => handleDragStart(index)}
                  onDragEnd={handleDragEnd}
                  onDragOver={(e) => handleDragOver(e, index)}
                >
                  {/* 拖拽手柄 */}
                  <div className="track-item__drag">
                    <DragIcon />
                  </div>

                  {/* 序号 */}
                  <div className="track-item__number">
                    <span>{index + 1}</span>
                  </div>

                  {/* 歌曲信息 */}
                  <div className="track-item__info">
                    <span className="track-item__title">{song.title || 'Untitled'}</span>
                    <span className="track-item__artist">{song.artist || 'Unknown Artist'}</span>
                  </div>

                  {/* 编辑按钮 */}
                  <button
                    className="track-item__edit"
                    onClick={() => handleEditSong(index)}
                  >
                    <EditIcon />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>No songs in this album yet. Click "Add More Songs" to get started.</p>
            </div>
          )}

          {/* 按钮区域 */}
          <div className="album-info__actions">
            {/* 添加更多歌曲按钮 */}
            <button className="add-songs-btn" onClick={handleAddMoreSongs}>
              <Icon style="Upload" />
              <span>Add More Songs</span>
            </button>

            {/* 保存按钮 */}
            <Button
              type="primary"
              onClick={handleSave}
              className="album-info__submit"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AlbumInfo;
